import { CCBoolean, CCFloat, Gradient, _decorator } from 'cc';
import { DEV } from 'cc/env';
import ITintObject from '../Shader/TintObject';
import TintParticle from '../Shader/TintParticle';
import TintSprite from '../Shader/TintSprite';
import MultiThemeComponent from './MultiThemeComponent';

const { ccclass, property } = _decorator;

@ccclass('SampleRangeSetting')
class SampleRangeSetting {
    @property
    public useSampleBound: boolean = false;
    @property({
        slide: true,
        range: [0, 1, 0.001],
        visible(this: SampleRangeSetting) {
            return this.useSampleBound;
        },
    })
    public sampleLeftBound: number = 0;
    @property({
        slide: true,
        range: [0, 1, 0.001],
        visible(this: SampleRangeSetting) {
            return this.useSampleBound;
        },
    })
    public sampleRightBound: number = 1;
}

@ccclass('MultiThemeTintObject')
export default class MultiThemeTintObject extends MultiThemeComponent<ITintObject> {
    @property
    protected readonly _copyGradientToMultiThemeTint = false;

    @property
    protected _copyGradientToIndex = -1;

    @property
    protected readonly updateGradientSets = true;

    @property({
        type: [Gradient],
        visible(this: MultiThemeTintObject) {
            return this.updateGradientSets;
        },
    })
    protected readonly gradientSets: Gradient[] = [];

    @property
    protected readonly updateUseOriginColorSets = false;

    @property({
        type: [CCBoolean],
        visible(this: MultiThemeTintObject) {
            return this.updateUseOriginColorSets;
        },
    })
    protected readonly useOriginColorSets: boolean[] = [];

    @property
    protected readonly updateGrayValueOffset = false;

    @property({
        type: [CCFloat],
        slide: true,
        range: [0, 1, 0.01],
        visible(this: MultiThemeTintObject) {
            return this.updateGrayValueOffset;
        },
    })
    protected readonly grayValueOffsetSets: number[] = [];

    @property
    protected readonly updateSampleBound = false;

    @property({
        type: [SampleRangeSetting],
        visible(this: MultiThemeTintObject) {
            return this.updateSampleBound;
        },
    })
    protected readonly sampleRangeSets: SampleRangeSetting[] = [];

    @property({ group: '設定 Gradient 工具', tooltip: DEV && '點擊後會複製 Tint Sprite/Particle 中的 Gradient 到 GradientSets (可透過設定的 Index 覆蓋)' })
    public get copyGradientToMultiThemeTint() {
        return this._copyGradientToMultiThemeTint;
    }
    public set copyGradientToMultiThemeTint(value: boolean) {
        if (value === this._copyGradientToMultiThemeTint) {
            return;
        }
        if (value && this.target) {
            if (this._copyGradientToIndex < 0 || this._copyGradientToIndex >= this.gradientSets.length) {
                this.gradientSets.push(this.target.tintGradient);
            } else {
                this.gradientSets[this._copyGradientToIndex] = this.target.tintGradient;
            }
            this._copyGradientToIndex = -1;
        }
    }

    @property({ group: '設定 Gradient 工具' })
    public get copyGradientToIndex() {
        return this._copyGradientToIndex;
    }
    public set copyGradientToIndex(value: number) {
        if (value === this._copyGradientToIndex) {
            return;
        }
        this._copyGradientToIndex = value;
    }

    public getMaxTheme(): number {
        if (this.updateGradientSets) {
            return this.gradientSets.length;
        }
        if (this.updateUseOriginColorSets) {
            return this.useOriginColorSets.length;
        }
        if (this.updateGrayValueOffset) {
            return this.grayValueOffsetSets.length;
        }
        if (this.updateSampleBound) {
            return this.sampleRangeSets.length;
        }
        return 0;
    }

    public resetInEditor() {
        if (this.target) {
            this.gradientSets.push(this.target.tintGradient);
        }
    }

    protected getTarget(): ITintObject {
        return this.getComponent(TintSprite) || this.getComponent(TintParticle);
    }

    protected onThemeChanged(): void {
        if (this.updateGradientSets && this._theme < this.gradientSets.length) {
            this.target.tintGradient = this.gradientSets[this.theme];
        }
        if (this.updateUseOriginColorSets && this._theme < this.useOriginColorSets.length) {
            this.target.useOriginColor = this.useOriginColorSets[this.theme];
        }
        if (this.updateGrayValueOffset && this._theme < this.grayValueOffsetSets.length) {
            this.target.grayValueOffset = this.grayValueOffsetSets[this.theme];
        }
        if (this.updateSampleBound && this._theme < this.sampleRangeSets.length) {
            const sampleRange = this.sampleRangeSets[this.theme];
            this.target.useSampleBound = sampleRange.useSampleBound;
            if (sampleRange.useSampleBound) {
                this.target.sampleLeftBound = sampleRange.sampleLeftBound;
                this.target.sampleRightBound = sampleRange.sampleRightBound;
            }
        }
    }
}
